export interface AssessmentCategory {
  name: string;
  score: number;
  maxScore: number;
  indicators: { label: string; value: number; notes: string }[];
}

export interface RiskAssessment {
  id: string;
  childId: string;
  childName: string;
  caseNumber: string;
  assessorName: string;
  assessmentDate: string;
  totalScore: number;
  riskLevel: 'low' | 'moderate' | 'high' | 'critical';
  categories: AssessmentCategory[];
  recommendations: string[];
  supervisorNotes: string;
  status: 'draft' | 'submitted' | 'approved';
  previousScores: { date: string; score: number }[];
}

export const mockAssessments: RiskAssessment[] = [
  {
    id: 'ra1',
    childId: 'ch1',
    childName: 'Juan Dela Cruz',
    caseNumber: 'DCPCMS-2026-00001',
    assessorName: 'Maria Santos',
    assessmentDate: '2026-04-09',
    totalScore: 87,
    riskLevel: 'critical',
    categories: [
      {
        name: 'Physical Safety',
        score: 22,
        maxScore: 25,
        indicators: [
          { label: 'Evidence of physical injury', value: 5, notes: 'Multiple bruises documented' },
          { label: 'Severity of injury', value: 4, notes: 'Moderate to severe bruising' },
          { label: 'Frequency of incidents', value: 5, notes: 'Recurring incidents reported' },
          { label: 'Access to medical care', value: 4, notes: 'Delayed medical attention' },
          { label: 'Immediate safety risk', value: 4, notes: 'Perpetrator still in household' },
        ],
      },
      {
        name: 'Emotional Well-being',
        score: 18,
        maxScore: 25,
        indicators: [
          { label: 'Signs of trauma/distress', value: 4, notes: 'Child shows fear and withdrawal' },
          { label: 'Behavioral changes', value: 4, notes: 'Aggressive behavior at school' },
          { label: 'Self-harm indicators', value: 3, notes: 'No self-harm observed' },
          { label: 'Social withdrawal', value: 4, notes: 'Avoids peers and adults' },
          { label: 'Age-appropriate development', value: 3, notes: 'Slight developmental delays' },
        ],
      },
      {
        name: 'Family Environment',
        score: 24,
        maxScore: 25,
        indicators: [
          { label: 'Perpetrator access to child', value: 5, notes: 'Lives in same household' },
          { label: 'Guardian protective capacity', value: 5, notes: 'Mother unable to protect child' },
          { label: 'Domestic violence present', value: 5, notes: 'Confirmed domestic violence' },
          { label: 'Substance abuse in household', value: 4, notes: 'Father reported alcohol use' },
          { label: 'Family support network', value: 5, notes: 'Isolated family, no support' },
        ],
      },
      {
        name: 'Educational Status',
        score: 23,
        maxScore: 25,
        indicators: [
          { label: 'School attendance', value: 5, notes: 'Frequent absences' },
          { label: 'Academic performance', value: 4, notes: 'Declining grades' },
          { label: 'School-reported concerns', value: 5, notes: 'Multiple teacher reports' },
          { label: 'Peer relationships', value: 5, notes: 'Isolated from peers' },
          { label: 'Teacher-student relationship', value: 4, notes: 'Distrustful of adults' },
        ],
      },
    ],
    recommendations: [
      'Immediate removal of child from abusive environment',
      'File criminal charges against perpetrator',
      'Provide emergency shelter or place with safe relative',
      'Initiate psychological counseling for child',
      'Coordinate with PNP WCPD for investigation',
      'Conduct weekly monitoring visits',
    ],
    supervisorNotes: 'Case requires immediate intervention. Approved for emergency protective custody.',
    status: 'approved',
    previousScores: [
      { date: '2026-01-20', score: 75 },
      { date: '2026-02-15', score: 80 },
      { date: '2026-03-10', score: 83 },
      { date: '2026-04-09', score: 87 },
    ],
  },
  {
    id: 'ra2',
    childId: 'ch2',
    childName: 'Ana Reyes',
    caseNumber: 'DCPCMS-2026-00002',
    assessorName: 'Maria Santos',
    assessmentDate: '2026-04-07',
    totalScore: 72,
    riskLevel: 'high',
    categories: [
      {
        name: 'Physical Safety',
        score: 15,
        maxScore: 25,
        indicators: [
          { label: 'Evidence of physical injury', value: 2, notes: 'No physical injuries' },
          { label: 'Severity of injury', value: 1, notes: 'N/A' },
          { label: 'Frequency of incidents', value: 3, notes: 'Ongoing neglect' },
          { label: 'Access to medical care', value: 4, notes: 'Limited access to healthcare' },
          { label: 'Immediate safety risk', value: 5, notes: 'Malnutrition is a health risk' },
        ],
      },
      {
        name: 'Emotional Well-being',
        score: 16,
        maxScore: 25,
        indicators: [
          { label: 'Signs of trauma/distress', value: 3, notes: 'Mild anxiety observed' },
          { label: 'Behavioral changes', value: 3, notes: 'Withdrawn at school' },
          { label: 'Self-harm indicators', value: 2, notes: 'None observed' },
          { label: 'Social withdrawal', value: 4, notes: 'Limited peer interaction' },
          { label: 'Age-appropriate development', value: 4, notes: 'Behind in studies' },
        ],
      },
      {
        name: 'Family Environment',
        score: 22,
        maxScore: 25,
        indicators: [
          { label: 'Perpetrator access to child', value: 5, notes: 'Guardian is the neglector' },
          { label: 'Guardian protective capacity', value: 4, notes: 'Aunt overwhelmed with responsibilities' },
          { label: 'Domestic violence present', value: 3, notes: 'No domestic violence' },
          { label: 'Substance abuse in household', value: 5, notes: 'Aunt reported alcohol use' },
          { label: 'Family support network', value: 5, notes: 'No extended family support' },
        ],
      },
      {
        name: 'Educational Status',
        score: 19,
        maxScore: 25,
        indicators: [
          { label: 'School attendance', value: 5, notes: 'Very frequent absences' },
          { label: 'Academic performance', value: 4, notes: 'Failing several subjects' },
          { label: 'School-reported concerns', value: 4, notes: 'Multiple reports from teachers' },
          { label: 'Peer relationships', value: 3, notes: 'Some friends at school' },
          { label: 'Teacher-student relationship', value: 3, notes: 'Trusts guidance counselor' },
        ],
      },
    ],
    recommendations: [
      'Provide nutritional support and food assistance',
      'Enroll guardian in parenting skills program',
      'Coordinate with LGU for social welfare assistance',
      'Monitor school attendance weekly',
      'Consider alternative care if situation does not improve',
    ],
    supervisorNotes: 'Pending review. Intervention plan needs approval.',
    status: 'submitted',
    previousScores: [
      { date: '2026-02-10', score: 68 },
      { date: '2026-03-15', score: 70 },
      { date: '2026-04-07', score: 72 },
    ],
  },
  {
    id: 'ra3',
    childId: 'ch6',
    childName: 'Liza Fernandez',
    caseNumber: 'DCPCMS-2026-00006',
    assessorName: 'Maria Santos',
    assessmentDate: '2026-04-09',
    totalScore: 91,
    riskLevel: 'critical',
    categories: [
      {
        name: 'Physical Safety',
        score: 25,
        maxScore: 25,
        indicators: [
          { label: 'Evidence of physical injury', value: 5, notes: 'Fractured arm documented' },
          { label: 'Severity of injury', value: 5, notes: 'Bone fracture - serious injury' },
          { label: 'Frequency of incidents', value: 5, notes: 'Suspected repeated abuse' },
          { label: 'Access to medical care', value: 5, notes: 'Hospitalized' },
          { label: 'Immediate safety risk', value: 5, notes: 'Perpetrator unidentified' },
        ],
      },
      {
        name: 'Emotional Well-being',
        score: 20,
        maxScore: 25,
        indicators: [
          { label: 'Signs of trauma/distress', value: 5, notes: 'Severe fear and anxiety' },
          { label: 'Behavioral changes', value: 4, notes: 'Regressive behavior' },
          { label: 'Self-harm indicators', value: 3, notes: 'None observed' },
          { label: 'Social withdrawal', value: 4, notes: 'Clings to hospital staff' },
          { label: 'Age-appropriate development', value: 4, notes: 'Trauma affecting development' },
        ],
      },
      {
        name: 'Family Environment',
        score: 23,
        maxScore: 25,
        indicators: [
          { label: 'Perpetrator access to child', value: 5, notes: 'Grandmother is primary suspect' },
          { label: 'Guardian protective capacity', value: 5, notes: 'Guardian is suspected perpetrator' },
          { label: 'Domestic violence present', value: 4, notes: 'Suspected violence in home' },
          { label: 'Substance abuse in household', value: 4, notes: 'Unconfirmed reports' },
          { label: 'Family support network', value: 5, notes: 'Mother abroad, no other relatives' },
        ],
      },
      {
        name: 'Educational Status',
        score: 23,
        maxScore: 25,
        indicators: [
          { label: 'School attendance', value: 5, notes: 'Currently hospitalized' },
          { label: 'Academic performance', value: 4, notes: 'Unknown due to hospitalization' },
          { label: 'School-reported concerns', value: 5, notes: 'Teacher reported prior concerns' },
          { label: 'Peer relationships', value: 5, notes: 'Unable to assess' },
          { label: 'Teacher-student relationship', value: 4, notes: 'Teacher concerned' },
        ],
      },
    ],
    recommendations: [
      'Immediate removal from guardian\'s custody',
      'Emergency placement with safe relative or foster care',
      'File criminal charges against suspected perpetrator',
      'Provide trauma-informed psychological support',
      'Contact mother abroad immediately',
      'Daily monitoring until safe placement secured',
    ],
    supervisorNotes: 'Critical case. Emergency intervention authorized.',
    status: 'approved',
    previousScores: [
      { date: '2026-03-28', score: 88 },
      { date: '2026-04-09', score: 91 },
    ],
  },
];
